import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Save } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightLegCatalogService,
  type CatalogFreightLeg,
  type CreateCatalogLegInput,
} from '../services/freight-leg-catalog-service';
import {
  TransportMode,
  FreightLegType,
  TRANSPORT_MODE_LABELS,
  FREIGHT_LEG_TYPE_LABELS,
} from '../types/freight-route.types';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  freightLocationsService,
  FREIGHT_LOCATION_TYPE_LABELS,
  type FreightLocation,
} from '../services/freight-locations-service';

type DialogMode = { kind: 'create' } | { kind: 'edit'; leg: CatalogFreightLeg };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: DialogMode;
  onSuccess: () => void;
}

const EMPTY_FORM: CreateCatalogLegInput & {
  isActive: boolean;
  originLocationId: string | null;
  destinationLocationId: string | null;
  sapCode: string | null;
} = {
  origin: '',
  destination: '',
  originLocationId: null,
  destinationLocationId: null,
  transportMode: 'SEA',
  legType: 'INTERNATIONAL',
  ratePerCbm: 0,
  ratePerKg: 0,
  ratePerContainer: null,
  minimumCharge: null,
  estimatedDays: null,
  providerId: null,
  isActive: true,
  sapCode: null,
};

export function LegCatalogFormDialog({ open, onOpenChange, mode, onSuccess }: Props) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [locations, setLocations] = useState<FreightLocation[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    freightLocationsService
      .list({ isActive: true })
      .then((data) => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const pickLocation = (kind: 'origin' | 'destination', id: string) => {
    const loc = locations.find((l) => l.id === id);
    if (kind === 'origin') {
      setForm((f) => ({ ...f, originLocationId: id, origin: loc?.name ?? f.origin }));
    } else {
      setForm((f) => ({
        ...f,
        destinationLocationId: id,
        destination: loc?.name ?? f.destination,
      }));
    }
  };

  // Reset form when dialog opens/mode changes
  useEffect(() => {
    if (!open) return;
    if (mode.kind === 'edit') {
      const { leg } = mode;
      setForm({
        origin: leg.origin,
        destination: leg.destination,
        originLocationId: (leg as { originLocationId?: string | null }).originLocationId ?? null,
        destinationLocationId:
          (leg as { destinationLocationId?: string | null }).destinationLocationId ?? null,
        transportMode: leg.transportMode,
        legType: leg.legType,
        ratePerCbm: leg.ratePerCbm,
        ratePerKg: leg.ratePerKg,
        ratePerContainer: leg.ratePerContainer,
        minimumCharge: leg.minimumCharge,
        estimatedDays: leg.estimatedDays,
        providerId: leg.providerId,
        isActive: leg.isActive,
        sapCode: (leg as any).sapCode ?? null,
      });
    } else {
      setForm({ ...EMPTY_FORM });
    }
  }, [open, mode]);

  const set = <K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.originLocationId && !(form.origin || '').trim()) {
      toast({ title: 'Origen es requerido', variant: 'destructive' });
      return;
    }
    if (!form.destinationLocationId && !(form.destination || '').trim()) {
      toast({ title: 'Destino es requerido', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      if (mode.kind === 'edit') {
        await freightLegCatalogService.update(mode.leg.id, form);
        toast({ title: 'Tramo actualizado', variant: 'success' });
      } else {
        await freightLegCatalogService.create(form);
        toast({ title: 'Tramo creado', variant: 'success' });
      }
      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode.kind === 'edit' ? 'Editar Tramo' : 'Nuevo Tramo de Catálogo'}
          </DialogTitle>
          <DialogDescription>
            {mode.kind === 'edit'
              ? 'Actualiza los datos y tarifas del tramo'
              : 'Define un tramo reutilizable independiente de cualquier ruta'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Origin / Destination */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Origen</Label>
              <SearchableSelect
                value={form.originLocationId ?? ''}
                onValueChange={(v) => pickLocation('origin', v)}
                placeholder="Seleccionar localización..."
                searchPlaceholder="Buscar..."
                emptyMessage="Cargá localizaciones en datos maestros"
                options={locations.map((l) => ({
                  value: l.id,
                  label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                }))}
              />
              {form.origin && !form.originLocationId && (
                <p className="text-[10px] text-muted-foreground italic">
                  Origen actual: {form.origin}
                </p>
              )}
            </div>
            <div>
              <Label>Destino</Label>
              <SearchableSelect
                value={form.destinationLocationId ?? ''}
                onValueChange={(v) => pickLocation('destination', v)}
                placeholder="Seleccionar localización..."
                searchPlaceholder="Buscar..."
                emptyMessage="Cargá localizaciones en datos maestros"
                options={locations.map((l) => ({
                  value: l.id,
                  label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                }))}
              />
              {form.destination && !form.destinationLocationId && (
                <p className="text-[10px] text-muted-foreground italic">
                  Destino actual: {form.destination}
                </p>
              )}
            </div>
          </div>

          {/* Mode / Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Modo de Transporte</Label>
              <Select
                value={form.transportMode}
                onValueChange={(v) => set('transportMode', v as TransportMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TRANSPORT_MODE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Tramo</Label>
              <Select
                value={form.legType}
                onValueChange={(v) => set('legType', v as FreightLegType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FREIGHT_LEG_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
            <strong className="text-foreground">Precios, proveedores y días de tránsito</strong> se
            configuran después de crear el tramo, desde el botón 🪙 en la tabla. Cada proveedor
            puede tener su propia tarifa, mínimo y tiempo de tránsito.
          </div>

          <div>
            <Label>Código SAP</Label>
            <Input
              value={form.sapCode ?? ''}
              onChange={(e) => set('sapCode', e.target.value || null)}
              placeholder="Código equivalente en SAP (opcional)"
              maxLength={64}
            />
          </div>

          {/* isActive — only shown when editing */}
          {mode.kind === 'edit' && (
            <div className="flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Tramo activo
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {mode.kind === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
